/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import _ from "lodash";
import {
  Typography,
  CircularProgress,
  Menu,
  MenuItem,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Accordion,
  AccordionDetails,
} from "@mui/material";
import { Formik } from "formik";
import Box from "@mui/material/Box";
import Logo from "../../assets/images/logo.svg";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import { BASE_URL } from "../../api/client";
import LogoutIcon from "@mui/icons-material/Logout";
import AddIcon from "@mui/icons-material/Add";
import LinearProgress from "@mui/material/LinearProgress";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import GradedEssayData from './GradedEssayData'; // Import the new component

function LinearProgressWithLabel(props) {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box sx={{ width: "100%", mr: 1 }}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" color="text.secondary">{`${Math.round(
          props.value
        )}%`}</Typography>
      </Box>
    </Box>
  );
}

LinearProgressWithLabel.propTypes = {
  value: PropTypes.number.isRequired,
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 0 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ITEM_HEIGHTS = 48;
const ITEM_PADDINGS_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHTS * 4.5 + ITEM_PADDINGS_TOP,
    },
  },
};

function QuestionDetailsPanel({ question }) {
  const [selectedPage, setSelectedPage] = useState(0);
  const pages = question.pages || [];

  return (
    <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
      {/* Question Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Question Details
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Typography><strong>Question:</strong> {question.question}</Typography>
          <Typography><strong>Points:</strong> {question.question_points}</Typography>
          <Typography><strong>Word Limit:</strong> {question.question_word_limit}</Typography>
          <Typography><strong>Score:</strong> {question.question_score}</Typography>
          <Typography><strong>Essay Type:</strong> {question.essay_type}</Typography>
          {question.question_scenario && (
            <Typography><strong>Scenario:</strong> {question.question_scenario}</Typography>
          )}
        </Box>

        {/* Score Checklist */}
        {question.score_checklist && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Score Checklist:</Typography>
            <Box sx={{ pl: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {Object.entries(question.score_checklist).map(([key, value]) => (
                <Typography key={key}>
                  <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong> {value}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        {/* Overall Feedback */}
        {question.overall_feedback?.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Overall Feedback:</Typography>
            <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
              {question.overall_feedback.map((feedback, idx) => (
                <li key={idx}>{feedback}</li>
              ))}
            </ul>
          </Box>
        )}
      </Paper>

      {/* Pages Navigation */}
      {pages.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Pages:</Typography>
          <Stack direction="row" spacing={1}>
            {pages.map((_, index) => (
              <Button
                key={index}
                variant={selectedPage === index ? 'contained' : 'outlined'}
                onClick={() => setSelectedPage(index)}
              >
                Page {index + 1}
              </Button>
            ))}
          </Stack>
        </Box>
      )}

      {/* Selected Page Content */}
      {pages[selectedPage] && (
        <Box>
          {/* Page Info */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Page {selectedPage + 1} Details
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Typography><strong>Page ID:</strong> {pages[selectedPage].page_id}</Typography>
              <Typography><strong>Page Number:</strong> {pages[selectedPage].page_number}</Typography>
              <Typography><strong>Handwritten Question:</strong> {pages[selectedPage].handwritten_question ? 'Yes' : 'No'}</Typography>
            </Box>
          </Paper>

          {/* Page Image */}
          {pages[selectedPage].image_url && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Page Image:</Typography>
              <img 
                src={pages[selectedPage].image_url} 
                alt={`Page ${selectedPage + 1}`}
                style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }}
              />
            </Paper>
          )}

          {/* Response Text */}
          {pages[selectedPage].response && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Response:</Typography>
              <Typography 
                component="pre" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                  m: 0
                }}
              >
                {pages[selectedPage].response}
              </Typography>
            </Paper>
          )}

          {/* Diagrams */}
          {pages[selectedPage].diagrams?.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Diagrams:</Typography>
              {pages[selectedPage].diagrams.map((diagram, idx) => (
                <Box key={idx} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
                  <Typography><strong>Description:</strong> {diagram.description}</Typography>
                  <Typography 
                    component="pre" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'inherit',
                      m: 0,
                      mt: 1 
                    }}
                  >
                    <strong>Content:</strong> {diagram.content}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}

          {/* Page Feedback */}
          {pages[selectedPage].feedback?.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Page Feedback:</Typography>
              <Stack spacing={2}>
                {pages[selectedPage].feedback.map((feedback, idx) => (
                  <Box key={idx} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography gutterBottom>{feedback.feedback}</Typography>
                    {feedback.priority !== undefined && (
                      <Typography variant="body2" color="text.secondary">
                        Priority: {feedback.priority}
                      </Typography>
                    )}
                    {feedback.bounding_boxes && Object.keys(feedback.bounding_boxes).length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Has bounding boxes: Yes
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
}

function EssayGradeView({ essays }) {
  const [selectedEssay, setSelectedEssay] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Essay Selection */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Select Essay</InputLabel>
        <Select
          value={selectedEssay || ''}
          onChange={(e) => {
            setSelectedEssay(e.target.value);
            setSelectedQuestion(null);
          }}
        >
          {essays.map((essay) => (
            <MenuItem key={essay.id} value={essay.id}>
              Essay {essay.id} - {essay.student.first_name} {essay.student.last_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedEssay && (
        <Box>
          {/* Essay Info */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Essay Details
            </Typography>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Typography>
                <strong>Student:</strong> {essays.find(e => e.id === selectedEssay).student.first_name} {essays.find(e => e.id === selectedEssay).student.last_name}
              </Typography>
              <Typography>
                <strong>Total Questions:</strong> {essays.find(e => e.id === selectedEssay).total_questions}
              </Typography>
              <Typography>
                <strong>Grade:</strong> {essays.find(e => e.id === selectedEssay).essay_data.graded_score}
              </Typography>
            </Box>
          </Paper>

          {/* Question Selection */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Questions
            </Typography>
            <Stack spacing={1}>
              {essays.find(e => e.id === selectedEssay).essay_data.question_responses.map((question, index) => (
                <Button
                  key={index}
                  variant={selectedQuestion === index ? 'contained' : 'outlined'}
                  onClick={() => setSelectedQuestion(index)}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  <Box>
                    <Typography variant="subtitle1">
                      Question {index + 1}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {question.question}
                    </Typography>
                  </Box>
                </Button>
              ))}
            </Stack>
          </Box>

          {/* Question Details */}
          {selectedQuestion !== null && (
            <QuestionDetailsPanel 
              question={essays.find(e => e.id === selectedEssay).essay_data.question_responses[selectedQuestion]} 
            />
          )}
        </Box>
      )}
    </Box>
  );
}

function EssayDetailsTable({ essay }) {
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  return (
    <TableContainer component={Paper} sx={{ mb: 4 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Question</TableCell>
            <TableCell>Points</TableCell>
            <TableCell>Word Limit</TableCell>
            <TableCell>Score</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {essay.essay_data.question_responses.map((question, index) => (
            <React.Fragment key={index}>
              <TableRow>
                <TableCell>{question.question || 'N/A'}</TableCell>
                <TableCell>{question.question_points}</TableCell>
                <TableCell>{question.question_word_limit}</TableCell>
                <TableCell>{question.question_score || 'Not graded'}</TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setExpandedQuestion(expandedQuestion === index ? null : index)}
                  >
                    {expandedQuestion === index ? 'Hide Details' : 'Show Details'}
                  </Button>
                </TableCell>
              </TableRow>
              {expandedQuestion === index && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <QuestionDetailsPanel question={question} />
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function GradedEssaysTable({ essays }) {
  const [selectedEssay, setSelectedEssay] = useState(null);

  return (
    <Box>
      {/* Essays Table */}
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Essay ID</TableCell>
              <TableCell>Graded Score</TableCell>
              <TableCell>Essay Type</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {essays && essays.map((essay) => (
              <React.Fragment key={essay.id || essay.essay_id}>
                <TableRow>
                  <TableCell>{essay.essay_data?.essay_id || essay.id}</TableCell>
                  <TableCell>{essay.essay_data?.graded_score}</TableCell>
                  <TableCell>{essay.essay_data?.essay_type}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      onClick={() => setSelectedEssay(essay.id)}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
                {selectedEssay === essay.id && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Box sx={{ p: 2 }}>
                        {/* Overall Essay Details */}
                        <Paper sx={{ p: 2, mb: 2 }}>
                          <Typography variant="h6" gutterBottom>
                            Essay Details
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography>
                              <strong>Essay ID:</strong> {essay.essay_data?.essay_id}
                            </Typography>
                            <Typography>
                              <strong>Graded Score:</strong> {essay.essay_data?.graded_score}
                            </Typography>
                            <Typography>
                              <strong>Essay Type:</strong> {essay.essay_data?.essay_type}
                            </Typography>
                          </Box>
                        </Paper>

                        {/* Question Responses */}
                        <Typography variant="h6" gutterBottom>
                          Question Responses
                        </Typography>
                        {essay.essay_data?.question_responses?.map((question, index) => (
                          <Paper key={index} sx={{ p: 2, mb: 2 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle1" gutterBottom>
                                Question {index + 1}
                              </Typography>
                              <Typography><strong>Question:</strong> {question.question}</Typography>
                              <Typography><strong>Points:</strong> {question.question_points}</Typography>
                              <Typography><strong>Score:</strong> {question.question_score}</Typography>
                              <Typography><strong>Word Limit:</strong> {question.question_word_limit}</Typography>
                            </Box>

                            {/* Overall Feedback */}
                            {question.overall_feedback?.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Overall Feedback:
                                </Typography>
                                <ul>
                                  {question.overall_feedback.map((feedback, idx) => (
                                    <li key={idx}>{feedback}</li>
                                  ))}
                                </ul>
                              </Box>
                            )}

                            {/* Pages */}
                            {question.pages?.length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  Pages:
                                </Typography>
                                {question.pages.map((page, pageIndex) => (
                                  <Box key={pageIndex} sx={{ mb: 2 }}>
                                    <Typography><strong>Page {pageIndex + 1}</strong></Typography>
                                    {page.image_url && (
                                      <img
                                        src={page.image_url}
                                        alt={`Page ${pageIndex + 1}`}
                                        style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }}
                                      />
                                    )}
                                    <Typography><strong>Response:</strong></Typography>
                                    <Typography component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {page.response}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Paper>
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function Grade() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [essays, setEssays] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [paperTypeAnchorEl, setPaperTypeAnchorEl] = useState(null);
  const [essayFilters, setEssayFilters] = useState({
    teacherid: "",
    studentid: "", 
  });
  const [parseEssayData, setParseEssayData] = useState({
    teacher: "",
    student: "",
    handwritten: "-1",
    paperType: "4", 
    numQuestions: "-1",
    moreInfo: "[]",
    pageNumbers: "[]",
    file: null,
  });
  const [gradeEssayId, setGradeEssayId] = useState("");
  const [loading, setLoading] = useState(false);
  const [isFullScreenLoading, setIsFullScreenLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [gradedEssayData, setGradedEssayData] = useState(null);

  useEffect(() => {
    let timer;
    if (isFullScreenLoading) {
      setProgress(0);
      const duration = 120; 
      const interval = 1000; 
      const increment = 100 / (duration * (1000 / interval)); 

      timer = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + increment;
          return newProgress >= 100 ? 100 : newProgress;
        });
      }, interval);

      // Clear timer after 2 minutes
      setTimeout(() => {
        clearInterval(timer);
      }, duration * 1000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [isFullScreenLoading]);

  const fetchEssays = async () => {
    try {
      setLoading(true);
      const params = {};
      if (essayFilters.teacherid) params.teacherid = essayFilters.teacherid;
      if (essayFilters.studentid) params.studentid = essayFilters.studentid;

      const response = await axios.get(`${BASE_URL}/getessays/`, { params });
      setEssays(response.data.essays || []);
      toast.success("Essays fetched successfully!");
    } catch (error) {
      console.error("Error fetching essays:", error);
      toast.error("Failed to fetch essays. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleParseEssay = async (values) => {
    try {
      setLoading(true);
      setIsFullScreenLoading(true);
      setProgress(0); // Reset progress

      const formData = new FormData();

      const payload = {
        teacher: String(values.teacher),
        student: String(values.student),
        handwritten: String(values.handwritten),
        paperType: "4",
        numQuestions: "-1",
        moreInfo: JSON.stringify([
          {
            question: 0,
            max_marks: 10,
            max_words: 100,
            special_instructions: "Follow the guidelines",
            model_solution: "This is a model solution",
            candq: "What is this?"
          }
        ]),
        pageNumbers: JSON.stringify([
          {
            question: 0,
            startPage: 1,
            endPage: 2
          }
        ]),
        file: values.file
      };

      Object.entries(payload).forEach(([key, value]) => {
        if (key === "file") {
          if (value) formData.append(key, value);
        } else {
          formData.append(key, value);
        }
      });

      const response = await axios.post(`${BASE_URL}/parseessay/`, formData);
      if (response.data.status === "success") {
        setProgress(100); // Set to 100% when complete
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait for animation
        toast.success(`Essay parsed successfully! Essay ID: ${response.data.essay_id}`);
        console.log("Essay data:", response.data.essay_data);
        setGradeEssayId(response.data.essay_id);
        setActiveTab(1);
      } else {
        toast.error("Error: " + response.data.message);
      }
    } catch (error) {
      toast.error("Error parsing essay: " + error.message);
    } finally {
      setLoading(false);
      setIsFullScreenLoading(false);
    }
  };

  const handleGradeEssay = async (essayId) => {
    try {
      setLoading(true);
      setIsFullScreenLoading(true);
      const formData = new FormData();
      formData.append("essay_id", essayId);

      const response = await axios.post(`${BASE_URL}/gradeessay/`, formData);
      if (response.data.status === "success") {
        console.log("Graded essay data:", response.data.essay_data);
        
        // Set the graded essay data to state
        setGradedEssayData(response.data.essay_data);

        toast.success(`Essay ${response.data.essay_id} graded successfully!`);
        setActiveTab(2); // Switch to the essay list tab
      } else {
        toast.error(`Error: ${response.data.message}`);
      }
    } catch (error) {
      toast.error("Error grading essay: " + error.message);
    } finally {
      setLoading(false);
      setIsFullScreenLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f6f9fc] to-[#eef2f7] p-6">
      {isFullScreenLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center backdrop-blur-sm transition-all duration-300">
          <div className="bg-white p-12 rounded-3xl shadow-2xl flex flex-col items-center transform transition-all duration-300 animate-fadeIn relative overflow-hidden w-[400px]">
            <div className="absolute inset-0 bg-gradient-to-tr from-blue-50/50 via-white to-blue-50/50" />
            
            <div className="relative z-10 w-full">
              <div className="relative w-20 h-20 mx-auto mb-8">
                <div className="absolute inset-0">
                  <div className="w-full h-full rounded-full border-4 border-blue-100" />
                  <div className="absolute top-0 left-0 w-full h-full rounded-full border-4 border-blue-500 border-t-transparent animate-spin" style={{ animationDuration: '1s' }} />
                  <div className="absolute top-0 left-0 w-full h-full rounded-full border-4 border-blue-300 border-t-transparent animate-spin" style={{ animationDuration: '1.5s' }} />
                </div>
                
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-ping" />
                </div>
              </div>

              <div className="text-center space-y-3">
                <div className="relative overflow-hidden h-8"> 
                  <div className="text-lg font-medium text-gray-800 tracking-wide">
                    {activeTab === 0 ? (
                      <div className="flex items-center justify-center animate-slideUp">
                        <span className="bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
                          Processing Essay
                        </span>
                        <div className="flex ml-2">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-blink" 
                                style={{ animationDelay: '0s' }} />
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-blink mx-1" 
                                style={{ animationDelay: '0.2s' }} />
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-blink" 
                                style={{ animationDelay: '0.4s' }} />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center animate-slideUp">
                        <span className="bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
                          Grading Essay
                        </span>
                        <div className="flex ml-2">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-blink" 
                                style={{ animationDelay: '0s' }} />
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-blink mx-1" 
                                style={{ animationDelay: '0.2s' }} />
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-blink" 
                                style={{ animationDelay: '0.4s' }} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <p className="text-sm text-gray-500 animate-fadeIn" 
                   style={{ animationDelay: '0.2s' }}>
                  Please wait while we analyze your document
                </p>
              </div>

              <div className="w-full space-y-2 mt-8">
                <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500 transition-all duration-300 ease-linear"
                    style={{
                      width: `${progress}%`,
                    }}
                  />
                </div>
                
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <span className="animate-pulse">
                    {progress < 100 ? 'Processing' : 'Completing...'}
                  </span>
                  <span className="font-medium">
                    {Math.round(progress)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8 flex justify-between items-center">
          <img src={Logo} alt="Logo" className="h-12" />
          <button
            onClick={() => navigate("/login")}
            className="flex items-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 transition-all shadow-sm hover:shadow-md"
          >
            <LogoutIcon className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>

        <h1 className="text-2xl font-bold text-gray-800 mb-8">
          Essay Grading Platform
        </h1>

        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="border-b border-gray-200 mb-8">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab(0)}
                className={`px-6 py-2 rounded-lg bg-white font-medium text-sm hover:bg-white ${
                  activeTab === 0
                    ? "text-gray-900"
                    : "text-gray-500"
                }`}
              >
                Parse Essay
              </button>
              <button
                onClick={() => setActiveTab(1)}
                className={`px-6 py-2 rounded-lg bg-white font-medium text-sm hover:bg-white ${
                  activeTab === 1
                    ? "text-gray-900"
                    : "text-gray-500"
                }`}
              >
                Grade Essay
              </button>
              <button
                onClick={() => {
                  fetchEssays(); // Fetch essays when the tab is clicked
                  setActiveTab(2);
                }}
                className={`px-6 py-2 rounded-lg bg-white font-medium text-sm hover:bg-white ${
                  activeTab === 2 ? "text-gray-900" : "text-gray-500"
                }`}
              >
                Graded Essay
              </button>
            </div>
          </div>

          <div className="mt-6">
            {activeTab === 0 && (
              <Formik
                initialValues={{
                  teacher: "",
                  student: "",
                  handwritten: "-1",
                  paperType: "4",
                  file: null,
                }}
                onSubmit={handleParseEssay}
              >
                {({ handleChange, handleBlur, handleSubmit, values, setFieldValue }) => (
                  <form onSubmit={handleSubmit} className="space-y-8 p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Teacher ID
                        </label>
                        <input
                          type="number"
                          name="teacher"
                          value={values.teacher}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          placeholder="Enter teacher ID"
                        />
                      </div>

                      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Student ID
                        </label>
                        <input
                          type="number"
                          name="student"
                          value={values.student}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          placeholder="Enter student ID"
                        />
                      </div>

                      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Handwritten
                        </label>
                        <button
                          type="button"
                          onClick={(e) => setAnchorEl(e.currentTarget)}
                          className="w-full px-4 py-3 rounded-lg bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 text-left flex items-center justify-between text-gray-700 hover:from-gray-100 hover:to-gray-200 transition-all shadow-sm"
                        >
                          {values.handwritten === "-1" ? "Ambiguous" : 
                           values.handwritten === "0" ? "Typed" : "Handwritten"}
                          <KeyboardArrowDownIcon />
                        </button>
                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={() => setAnchorEl(null)}
                          PaperProps={{
                            style: {
                              width: anchorEl?.offsetWidth,
                              marginTop: '4px',
                              borderRadius: '8px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          <MenuItem onClick={() => {
                            setFieldValue("handwritten", "-1");
                            setAnchorEl(null);
                          }}>Ambiguous</MenuItem>
                          <MenuItem onClick={() => {
                            setFieldValue("handwritten", "0");
                            setAnchorEl(null);
                          }}>Typed</MenuItem>
                          <MenuItem onClick={() => {
                            setFieldValue("handwritten", "1");
                            setAnchorEl(null);
                          }}>Handwritten</MenuItem>
                        </Menu>
                      </div>

                      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Paper Type
                        </label>
                        <button
                          type="button"
                          onClick={(e) => setPaperTypeAnchorEl(e.currentTarget)}
                          className="w-full px-4 py-3 rounded-lg bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 text-left flex items-center justify-between text-gray-700 hover:from-gray-100 hover:to-gray-200 transition-all shadow-sm"
                        >
                          {`Type ${values.paperType}`}
                          <KeyboardArrowDownIcon />
                        </button>
                        <Menu
                          anchorEl={paperTypeAnchorEl}
                          open={Boolean(paperTypeAnchorEl)}
                          onClose={() => setPaperTypeAnchorEl(null)}
                          PaperProps={{
                            style: {
                              width: paperTypeAnchorEl?.offsetWidth,
                              marginTop: '4px',
                              borderRadius: '8px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          <MenuItem onClick={() => {
                            setFieldValue("paperType", "4");
                            setPaperTypeAnchorEl(null);
                          }}>Type 4</MenuItem>
                          <MenuItem onClick={() => {
                            setFieldValue("paperType", "5");
                            setPaperTypeAnchorEl(null);
                          }}>Type 5</MenuItem>
                          <MenuItem onClick={() => {
                            setFieldValue("paperType", "6");
                            setPaperTypeAnchorEl(null);
                          }}>Type 6</MenuItem>
                        </Menu>
                      </div>
                    </div>

                    <div className="mt-8">
                      <div 
                        className="border-2 border-dashed border-gray-200 rounded-xl p-12 text-center cursor-pointer hover:border-blue-500 transition-colors group relative overflow-hidden bg-gradient-to-br from-gray-50 to-white"
                        onClick={() => document.getElementById('file-upload').click()}
                      >
                        <input
                          id="file-upload"
                          type="file"
                          accept=".pdf"
                          onChange={(e) => setFieldValue("file", e.target.files[0])}
                          className="hidden"
                        />
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        
                        <div className="relative z-10">
                          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                            <UploadFileIcon className="w-8 h-8 text-blue-500 group-hover:text-blue-600 transition-colors" />
                          </div>
                          
                          <h3 className="text-lg font-semibold text-gray-800 mb-2">
                            {values.file?.name || 'Upload PDF File'}
                          </h3>
                          
                          <p className="text-gray-500 text-sm">
                            {values.file 
                              ? 'Click to change file' 
                              : 'Click to select or drag & drop your PDF here'}
                          </p>
                          
                          {values.file && (
                            <div className="mt-4 inline-flex items-center gap-2 text-sm text-blue-600">
                              <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
                              File ready to upload
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end mt-8">
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-8 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 transition-all flex items-center gap-2 shadow-sm hover:shadow-md"
                      >
                        {loading ? (
                          <>
                            <CircularProgress size={20} className="text-white" />
                            <span>Parsing Essay...</span>
                          </>
                        ) : (
                          <>
                            <AddIcon className="w-5 h-5" />
                            <span>Parse Essay</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </Formik>
            )}

            {activeTab === 1 && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-white to-blue-50/50 rounded-xl p-5 shadow-sm border border-gray-100 mx-auto">
                  <div className="flex items-center justify-center gap-3">
                    <div className="flex items-center gap-3">
                      <label className="text-sm font-medium text-gray-600 whitespace-nowrap">
                        Essay ID:
                      </label>
                      <div className="relative w-40">
                        <input
                          type="number"
                          value={gradeEssayId}
                          onChange={(e) => setGradeEssayId(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 
                                   focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                                   bg-white/70 backdrop-blur-sm transition-all duration-300
                                   placeholder-gray-400 text-gray-700 text-sm"
                          placeholder="Enter ID"
                        />
                        {gradeEssayId && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Grade Button */}
                    <button
                      onClick={() => handleGradeEssay(gradeEssayId)}
                      disabled={loading || !gradeEssayId}
                      className="px-4 py-2 rounded-lg font-medium text-sm
                               disabled:opacity-50 disabled:cursor-not-allowed
                               transition-all duration-300 ease-in-out
                               flex items-center justify-center gap-2
                               bg-gradient-to-r from-blue-500 to-blue-600
                               hover:from-blue-600 hover:to-blue-700
                               text-white shadow-sm hover:shadow-md
                               disabled:hover:shadow-none"
                    >
                      {loading ? (
                        <>
                          <CircularProgress size={16} className="text-white" />
                          <span>Grading...</span>
                        </>
                      ) : (
                        <>
                          <AddIcon className="w-4 h-4" />
                          <span>Grade Essay</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 2 && (
              <Box>
                {gradedEssayData ? ( // Check if there is graded essay data
                  <GradedEssayData essayData={gradedEssayData} /> // Use the new component
                ) : (
                  <Typography>No graded essay available.</Typography>
                )}
              </Box>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Grade;